/**
 * Analytics Event Definitions
 *
 * Type-safe event tracking for Google Analytics 4
 */

// Event categories following the app's domain structure
export enum EventCategory {
  AUTH = 'auth',
  CLIENT = 'client',
  CASE = 'case',
  BITACORA = 'bitacora',
  ACCOUNTING = 'accounting',
  USER_MANAGEMENT = 'user_management',
  NAVIGATION = 'navigation',
  INTERACTION = 'interaction',  // For button clicks and UI interactions
}

// Standard GA4 events
export type StandardEvent =
  | 'login'
  | 'sign_up'
  | 'search'
  | 'page_view';

// Base interface for custom events
export interface CustomEvent {
  category: EventCategory;
  action: string;
  label?: string;
  value?: number;
  [key: string]: any;  // Additional custom parameters
}

// Button click event interface
export interface ButtonClickEvent extends CustomEvent {
  category: EventCategory.INTERACTION;
  action: 'button_click';
  label: string;
  button_text: string;
  button_location: string;
  button_action?: string;
  button_id?: string;
  page_path: string;
}

/**
 * Predefined Events
 *
 * Use these constants for consistent event tracking across the app
 */
export const AnalyticsEvents = {
  // ==========================================
  // AUTH EVENTS
  // ==========================================
  LOGIN_SUCCESS: {
    category: EventCategory.AUTH,
    action: 'login_success',
  },
  LOGIN_FAILURE: {
    category: EventCategory.AUTH,
    action: 'login_failure',
  },
  SIGNUP_SUCCESS: {
    category: EventCategory.AUTH,
    action: 'signup_success',
  },
  LOGOUT: {
    category: EventCategory.AUTH,
    action: 'logout',
  },

  // ==========================================
  // CLIENT EVENTS
  // ==========================================
  CLIENT_CREATED: {
    category: EventCategory.CLIENT,
    action: 'client_created',
  },
  CLIENT_VIEWED: {
    category: EventCategory.CLIENT,
    action: 'client_viewed',
  },
  CLIENT_UPDATED: {
    category: EventCategory.CLIENT,
    action: 'client_updated',
  },
  CLIENT_DELETED: {
    category: EventCategory.CLIENT,
    action: 'client_deleted',
  },

  // ==========================================
  // CASE EVENTS
  // ==========================================
  CASE_CREATED: {
    category: EventCategory.CASE,
    action: 'case_created',
  },
  CASE_VIEWED: {
    category: EventCategory.CASE,
    action: 'case_viewed',
  },
  CASE_UPDATED: {
    category: EventCategory.CASE,
    action: 'case_updated',
  },
  CASE_STATUS_CHANGED: {
    category: EventCategory.CASE,
    action: 'case_status_changed',
  },
  CASE_DELETED: {
    category: EventCategory.CASE,
    action: 'case_deleted',
  },

  // ==========================================
  // BITACORA EVENTS
  // ==========================================
  BITACORA_ENTRY_CREATED: {
    category: EventCategory.BITACORA,
    action: 'entry_created',
  },
  BITACORA_ENTRY_UPDATED: {
    category: EventCategory.BITACORA,
    action: 'entry_updated',
  },
  BITACORA_ENTRY_DELETED: {
    category: EventCategory.BITACORA,
    action: 'entry_deleted',
  },

  // ==========================================
  // ACCOUNTING EVENTS
  // ==========================================
  PAYMENT_RECORDED: {
    category: EventCategory.ACCOUNTING,
    action: 'payment_recorded',
  },
  RECEIPT_GENERATED: {
    category: EventCategory.ACCOUNTING,
    action: 'receipt_generated',
  },
  PAYMENT_STATUS_CHANGED: {
    category: EventCategory.ACCOUNTING,
    action: 'payment_status_changed',
  },

  // ==========================================
  // USER MANAGEMENT EVENTS
  // ==========================================
  USER_INVITED: {
    category: EventCategory.USER_MANAGEMENT,
    action: 'user_invited',
  },
  ROLE_CHANGED: {
    category: EventCategory.USER_MANAGEMENT,
    action: 'role_changed',
  },
  USER_DELETED: {
    category: EventCategory.USER_MANAGEMENT,
    action: 'user_deleted',
  },

  // ==========================================
  // NAVIGATION EVENTS
  // ==========================================
  SEARCH_PERFORMED: {
    category: EventCategory.NAVIGATION,
    action: 'search_performed',
  },
  FILTER_APPLIED: {
    category: EventCategory.NAVIGATION,
    action: 'filter_applied',
  },
  SORT_CHANGED: {
    category: EventCategory.NAVIGATION,
    action: 'sort_changed',
  },
} as const;
